import { fetchWithTimeout, parseErrorResponse } from './base.ts';
import type {
  Organization,
  OrganizationBranding,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationLicense,
  OrganizationLicenseStats,
} from '../../types.ts';

// Re-export zodat bestaande imports (bv. `import { Organization } from
// 'services/api/organizations'`) blijven werken — definitie staat in
// src/types.ts (plan 3 task A1, Refs #778).
export type {
  Organization,
  OrganizationBranding,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationLicense,
  OrganizationLicenseStats,
};

export const organizationsApi = {
  getOrganizations: async (): Promise<Organization[]> => {
    const res = await fetchWithTimeout('/api/organizations');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Plan 3 (#778): accepteert een object met name/slug/description. Backwards-
   * compat overload — bestaande callers `createOrganization(name, slug)` blijven
   * werken zolang de eerste parameter een string is.
   */
  createOrganization: async (
    nameOrData: string | { name: string; slug: string; description?: string },
    slug?: string
  ): Promise<Organization> => {
    const body =
      typeof nameOrData === 'string' ? { name: nameOrData, slug: slug ?? '' } : nameOrData;
    const res = await fetchWithTimeout('/api/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  updateOrganization: async (
    orgId: string,
    data: { name?: string; slug?: string; description?: string }
  ): Promise<Organization> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getOrganization: async (orgId: string): Promise<Organization> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}`);
    if (!res.ok) await parseErrorResponse(res);
    const serverOrg = await res.json();
    // Refs #842 — map server camelCase + snake_case shape naar de aliases die
    // de #842 redesign-componenten verwachten (created_at/poolSize/tier).
    // Bestaande velden blijven onveranderd zodat oude consumers niet breken.
    return {
      ...serverOrg,
      created_at: serverOrg.created_at ?? serverOrg.createdAt,
      poolSize: serverOrg.poolSize ?? serverOrg.license_pool_size,
      tier: serverOrg.tier ?? serverOrg.subscription_tier ?? null,
    };
  },

  updateBranding: async (
    orgId: string,
    branding: Partial<OrganizationBranding>
  ): Promise<{ branding: OrganizationBranding }> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/branding`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(branding),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  uploadLogo: async (
    orgId: string,
    fileData: string,
    mimeType: string
  ): Promise<{ success: boolean; logoUrl: string }> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileData, mime_type: mimeType }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteLogo: async (orgId: string): Promise<{ success: boolean }> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/logo`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // Members
  getMembers: async (orgId: string): Promise<OrganizationMember[]> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members`);
    if (!res.ok) await parseErrorResponse(res);
    const serverMembers = await res.json();
    // Refs #842 — server retourneert canonical camelCase (`accountId`,
    // `displayName`, `joinedAt`) + nested `license: { billing_period,
    // assigned_at }`. De #842 redesign-cards (MemberCard, MembersHero) lezen
    // snake_case + flat alias-velden. Mapping in de API-client houdt de
    // server-contract stabiel en de UI-laag schoon.
    if (!Array.isArray(serverMembers)) return serverMembers;
    return serverMembers.map((m: any) => ({
      ...m,
      account_id: m.account_id ?? m.accountId,
      name: m.name ?? m.displayName,
      joined_at: m.joined_at ?? m.joinedAt,
      billing_period: m.billing_period ?? m.license?.billing_period ?? null,
      // #1910 — de server levert het einde van het betaalde jaar. Terugvallen op de
      // toewijzingsdatum liet "t/m" een datum in het verleden tonen vlak na een betaling.
      license_expires_at: m.license_expires_at ?? null,
    }));
  },

  removeMember: async (orgId: string, accountId: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members/${accountId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Plan 3 (#778): wijzig de rol van een organisatie-lid (admin <-> member).
   * Naam onderscheidt zich expliciet van `collaborationApi.updateMemberRole`
   * (project-rollen) zodat de barrel-merge in ApiService.ts geen overschrijft.
   * Owner-only aan serverzijde: PUT /api/organizations/:orgId/members/:accountId
   * (toegevoegd na de audit van 2026-09-01, bevinding B2).
   */
  /**
   * Zelf de organisatie verlaten (audit 2026-09-01, P2). De eigenaar kan dit
   * niet: die draagt eerst over of verwijdert de organisatie.
   */
  /**
   * Zet de instelling "leden werken alleen in organisatieprojecten".
   * Owner-only aan serverzijde.
   */
  setMembersOrgProjectsOnly: async (orgId: string, value: boolean) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ members_org_projects_only: value }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  leaveOrganization: async (orgId: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/leave`, { method: 'POST' });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  updateOrgMemberRole: async (orgId: string, accountId: string, newRole: 'admin' | 'member') => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members/${accountId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // Invitations
  inviteOrgMember: async (orgId: string, email: string, role: string = 'member') => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Plan 3 (#778): uitgebreid invite-payload met billing_period en
   * personal_message. Backend (POST /:orgId/invitations) accepteert deze
   * velden nog niet expliciet — server negeert onbekende velden, dus payload
   * is alvast voorbereid voor wanneer plan 4 het toevoegt.
   */
  createInvitation: async (
    orgId: string,
    data: {
      email: string;
      role: 'admin' | 'member';
      billing_period?: 'annual' | 'monthly';
      personal_message?: string;
    }
  ): Promise<OrganizationInvitation> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getInvitations: async (orgId: string): Promise<OrganizationInvitation[]> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Alias gebruikt door useOrganizationStore (plan 3). Naam onderscheidt zich
   * expliciet van `collaborationApi.getPendingInvitations()` (project-invites,
   * geen orgId-arg) zodat de barrel-merge in ApiService.ts geen overschrijft.
   */
  getOrgPendingInvitations: async (orgId: string): Promise<OrganizationInvitation[]> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  cancelInvitation: async (orgId: string, inviteId: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations/${inviteId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getMyOrgInvitations: async () => {
    const res = await fetchWithTimeout('/api/organization-invitations');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  acceptOrgInvitation: async (inviteId: string) => {
    const res = await fetchWithTimeout(`/api/organization-invitations/${inviteId}/accept`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  declineOrgInvitation: async (inviteId: string) => {
    const res = await fetchWithTimeout(`/api/organization-invitations/${inviteId}/decline`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // ── Plan 4 (#778) — publieke token-lookup + accept/decline-by-id ────────────

  /**
   * Plan 4 (#778): publieke lookup van invitation-detail via token. Geen
   * credentials, geen auth-header — backend rate-limit beschermt tegen brute
   * force. Antwoord bevat `account_exists` zodat de UI mini-register vs login
   * kan kiezen.
   */
  getInvitationByToken: async (token: string) => {
    const res = await fetch(`/api/organization-invitations/by-token/${encodeURIComponent(token)}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Plan 4 (#778): rotate-token + opnieuw versturen van bestaande invite.
   * Backend regenereert `accept_token` zodat oude link ongeldig wordt.
   *
   * Naam-suffix `Org` voorkomt collision met `collaborationApi.resendInvitation`
   * (project-invites, single-arg) in de ApiService-barrel-merge.
   */
  resendOrgInvitation: async (orgId: string, invitationId: string) => {
    const res = await fetchWithTimeout(
      `/api/organizations/${orgId}/invitations/${invitationId}/resend`,
      { method: 'POST' }
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Plan 4 (#778): expliciete revoke (DELETE met token-invalidate semantics op
   * server). `cancelInvitation` blijft bestaan voor plan-3 callers; suffix
   * `Org` voorkomt collision met `collaborationApi.revokeInvitation`.
   */
  revokeOrgInvitation: async (orgId: string, invitationId: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // ── Plan 2 endpoints (Refs #778) ──────────────────────────────────────────
  // License-management — toegekend door owner/admin via routes/organizations.js.

  assignLicense: async (orgId: string, accountId: string, billingPeriod: 'annual' | 'monthly') => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members/${accountId}/license`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ billing_period: billingPeriod }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  revokeLicense: async (orgId: string, accountId: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members/${accountId}/license`, {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  changeLicensePeriod: async (
    orgId: string,
    accountId: string,
    billingPeriod: 'annual' | 'monthly'
  ) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/members/${accountId}/license`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ billing_period: billingPeriod }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  setPoolSize: async (orgId: string, annualSize: number) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/pool`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ annual_size: annualSize }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  openStripePortal: async (orgId: string): Promise<{ url: string }> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/stripe-portal`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  transferOwnership: async (orgId: string, newOwnerAccountId: string, confirmation: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/transfer-ownership`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        new_owner_account_id: newOwnerAccountId,
        confirmation,
      }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteOrg: async (orgId: string, confirmation: string) => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmation }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
